// src/app/modules/user/components/dashboard/PatientCountByCareMinsGrpChart.tsx

import React, {useEffect, useState} from 'react'
import {BarChart, Bar, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, LabelList} from 'recharts'
import {useNavigate} from 'react-router-dom' // ← import
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useReduxAuth} from '../../../auth'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import {USER_ROLES} from '../../../../constants/StatusConstants'

interface ChartData {
  category: string
  value: number
}

// your existing color map
const COLORS: Record<string, string> = {
  '0-9': '#d3d3d3',
  '10-19': '#f0ad4e',
  '20-29': '#95cbec',
  '30-39': '#95cbec',
  '40-49': '#45ba45',
  '50-59': '#45ba45',
  '60+': '#0c580c',
}

// 2. map each bar category to the filter value
const categoryToFilterMap: Record<string, string> = {
  '0-9': '0-9',
  '10-19': '10-19',
  '20-29': '20-29',
  '30-39': '30-39',
  '40-49': '40-49',
  '50-59': '50-59',
  '60+': '60',
}

const PatientCountByCareMinsGrpChart: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const navigate = useNavigate() // ← init
  const [chartData, setChartData] = useState<ChartData[]>([])

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
        setChartData([])
        return
      }

      try {
        // Build query params for MA
        const queryParams = new URLSearchParams()
        if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
          queryParams.append('doctor_id', maSelectedDoctorId.toString())
        }

        const response = await axios.get(
          API_ENDPOINTS.GET_PATIENT_COUNT_BY_CARE_MINS_GROUPS(queryParams.toString())
        )
        const breakdown: Record<string, number> = response.data.timeGroups
        const transformed: ChartData[] = Object.entries(breakdown).map(([key, value]) => ({
          category: key,
          value,
        }))
        setChartData(transformed)
      } catch (error) {
        console.error('Failed to fetch billing data', error)
      }
    }
    
    if (currentUser?.id || (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId)) {
      fetchData()
    }
  }, [currentUser, maSelectedDoctorId])

  // 3. click handler
  const handleBarClick = (category: string) => {
    const filterValue = categoryToFilterMap[category]
    if (filterValue) {
      navigate(`/patient?timeSpent=${encodeURIComponent(filterValue)}`)
    }
  }

  return (
    <div
      className='p-4 bg-white rounded-4 shadow-sm'
      style={{border: '1px solid #E0E0E0', width: '100%'}}
    >
      <div className='d-flex align-items-center gap-2 mb-3'>
        <h3>Time-Spent By Patient</h3>
        <span className='text-muted'>(99470/99457/99458 by Patient Billing Cycle)</span>
      </div>

      <ResponsiveContainer width='100%' height={350}>
        <BarChart data={chartData} margin={{top: 20, right: 0, left: 0, bottom: 20}} barGap={10}>
          <XAxis
            dataKey='category'
            label={{
              value: 'Time Spent (minutes)',
              position: 'insideBottom',
              offset: -5,
              dy: 5,
            }}
          />
          <YAxis
            allowDecimals={false}
            label={{
              value: 'Number of Patients',
              angle: -90,
              position: 'insideLeft',
              dx: 20,
              dy: 50,
            }}
          />
          <Tooltip />
          <Bar dataKey='value' name='Patient Count'>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.category] || '#8884d8'}
                style={{cursor: 'pointer'}} // ← pointer cursor
                onClick={() => handleBarClick(entry.category)} // ← click
              />
            ))}
            <LabelList dataKey='value' position='top' style={{fontSize: '12px', fill: '#333'}} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default PatientCountByCareMinsGrpChart
